"use client";

import React, { useState, useEffect } from "react";
import { Menu } from "lucide-react";
import { CartIcon } from "@/components/shop/CartIcon";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="25879" className="flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 animate-pulse rounded-md bg-secondary" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map(item => <NavigationMenuItem key={item.id}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative block px-4 py-2 text-base font-semibold text-primary transition-colors hover:text-chart-2 font-serif">
                {item.label}
                <span className="bg-chart-2 absolute bottom-0 left-0 h-[2px] w-full origin-center scale-x-0 transition-transform duration-300 group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
        <NavigationMenuItem>
          <CartIcon variant="ghost" size="icon" className="text-primary hover:text-chart-2 transition-colors" />
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 pt-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-full animate-pulse rounded-md bg-secondary" />)}
      </div>;
  }
  return <div className="flex flex-col gap-6 pt-8">
      {menuItems.map(item => <Link key={item.id} to={item.href || "#"} className="text-lg font-semibold text-primary transition-colors hover:text-chart-2 font-serif">
          {item.label}
        </Link>)}
      <CartIcon variant="ghost" size="icon" className="text-primary self-start" />
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="site-navigation" className={cn("sticky top-0 z-50 w-full border-b transition-all duration-300", isScrolled ? "border-transparent bg-background/85 shadow-sm backdrop-blur-md" : "border-border/40 bg-background")}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4">
        {}
        <Link to="/" className="flex-shrink-0">
          <img src="/img/aclc_college_logo.png" alt="PDA Australia" className="h-[40px] w-auto object-contain" />
        </Link>

        {}
        <div className="hidden md:block">
          <MenuProvider menu_id="184">
            <DesktopMenu />
          </MenuProvider>
        </div>

        {}
        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-primary">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Toggle navigation</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <SheetHeader>
                <SheetTitle className="text-left font-serif text-primary">Navigation</SheetTitle>
              </SheetHeader>
              <MenuProvider menu_id="184">
                <MobileMenu />
              </MenuProvider>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>;
}